import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { teamSelectors } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope } from "@fitness-app/data-models";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { usePermission } from "~/hooks/usePermission";
import { useAppSelector } from "~/store/initializeStore";
import { getCalendarTrainers } from "./calendarTrainers";

const TRAINER_PARAM = "trainerId";

/**
 * Whose consultations the calendar shows. Picking another trainer mirrors the read policy on
 * `calendar_event`: `clients.read` ALL ("staff read calendar events by permission"; the owner,
 * head coaches and administration hold it), and a head coach is offered only their own team.
 * Everyone else sees only their own rows, which is all RLS would return to them anyway. The pick
 * lives in the URL, so a link opens the same trainer.
 */
export const useCalendarTrainer = () => {
  const { userId } = useUserRole();
  const { memberId, isSharedTrainer } = useUserClaims();
  const canPickTrainer = usePermission(PermissionKey.CLIENTS_READ, PermissionScope.ALL);
  const team = useAppSelector(teamSelectors.getTrainersTeam);
  const [searchParams, setSearchParams] = useSearchParams();

  const trainers = useMemo(
    () => getCalendarTrainers(team ?? [], isSharedTrainer ? (memberId ?? null) : null),
    [team, isSharedTrainer, memberId],
  );

  const requestedId = searchParams.get(TRAINER_PARAM);
  // A head coach opens on their own calendar, the owner on the busiest trainer.
  const defaultTrainer = trainers.find((member) => member.id === memberId) ?? trainers[0];
  const selectedTrainer = canPickTrainer
    ? (trainers.find((member) => member.id === requestedId) ?? defaultTrainer ?? null)
    : null;

  const selectTrainer = useCallback(
    (trainerId: string) =>
      setSearchParams(
        (params) => {
          params.set(TRAINER_PARAM, trainerId);
          return params;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  return {
    canPickTrainer,
    trainers,
    selectedTrainer,
    selectTrainer,
    trainerUserId: canPickTrainer ? (selectedTrainer?.userId ?? null) : userId || null,
  };
};
